import LineItemFormTab from '@/Enums/CampaignManagement/LineItemFormTab.enum';
import { IDV360TicketsLineItemForm } from '@/Interfaces/DV360Tickets/LineItem.interface';
import type { IProduct } from '@/Interfaces/Product.interface';
import BlurBackdrop from '@/components/Backdrop/BlurBackdrop';
import DuplicateButton from '@/components/Buttons/DuplicateButton';
import CheckIcon from '@/components/Icons/checkIcon';
import { NEW_ITEM_ID } from '@/constants/campaignManagement';
import { dv360TicketsLineItemToFormStructure } from '@/helpers/dv360Tickets/campaignManagement/lineItemForm';
import DuplicateDV360TicketsLineItemModal from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/DV360LineItemDuplicationModal';
import DV360TicketsForecasterWidget from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/ForecasterWidget';
import DV360LineItemStatusChangeButton from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/components/LineItemStatusChangeButton';
import {
  DV360_LINE_ITEM_TABS_COMPONENTS,
  defaultFormData,
} from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/constants/';
import {
  DV360_DEFAULT_ACTIVE_TAB,
  DV360_LINE_ITEM_TABS,
} from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/constants/lineItemTabs';
import { getDirtyTabs } from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/helper';
import { dv360TicketsLineItemFormValidationSchema } from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/validationSchema';
import LineItemStatus from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/components/LineItemStatus';
import SavingDots from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/components/SavingDots';
import { EXPAND_ICON_CLASSNAME } from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/constants/';
import UserTypeCheckbox from '@/pages/UserManagement/Components/UserTypeCheckbox';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectShowBidCpmControl } from '@/store/selectors/currentWhitelabel.selectors';
import { selectDV360TicketsLineItem } from '@/store/selectors/dv360Tickets/insertionOrders.selectors';
import { getDv360TicketsInsertionOrderData } from '@/store/slices/dv360Tickets/insertionOrderManagement.slice';
import { yupResolver } from '@hookform/resolvers/yup';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Button, Collapse, Paper } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';

interface ISaveData {
  formData: IDV360TicketsLineItemForm;
  changedValues: Partial<IDV360TicketsLineItemForm>;
}

interface IProps {
  id: number | string;
  index: number;
  onSave: (data: ISaveData) => Promise<boolean>;
  defaultExpanded?: boolean;
  readOnly?: boolean;
}

const DV360LineItemForm = memo(({ id, index, onSave, defaultExpanded = false, readOnly }: IProps) => {
  const { campaignId } = useParams();

  const dispatch = useAppDispatch();

  const ref = useRef<HTMLDivElement>(null);

  const showBidCpm = useAppSelector(selectShowBidCpmControl);

  const resolver = useMemo(() => yupResolver(dv360TicketsLineItemFormValidationSchema(showBidCpm)), [showBidCpm]);

  const methods = useForm({
    // @ts-ignore
    resolver,
    defaultValues: defaultFormData,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    getValues,
    reset,
    control,
    formState: { isValid, isDirty, dirtyFields },
  } = methods;

  const getFormData = useCallback((): ISaveData => {
    const formData = getValues() as IDV360TicketsLineItemForm;
    const changedValues = Object.keys(dirtyFields).reduce(
      (acc, key) => {
        const typedKey = key as keyof IDV360TicketsLineItemForm;

        if (key === 'audiences') {
          acc.audiences = formData.audiences;
          return acc;
        }

        acc[typedKey] = formData[typedKey] as any;
        return acc;
      },
      {} as Partial<IDV360TicketsLineItemForm>,
    );

    return {
      formData,
      changedValues,
    };
  }, [getValues, dirtyFields]);

  const watchName = useWatch({ control, name: 'name' });
  const displayName = watchName || `Line item ${index + 1}`;
  const product = useWatch({ control, name: 'product' }) as IProduct | null;

  const [activeTab, setActiveTab] = useState(DV360_DEFAULT_ACTIVE_TAB);
  const [savedTabs, setSavedTabs] = useState<LineItemFormTab[]>([]);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isLoading, setIsLoading] = useState(false);
  const [isLineItemDuplicationModalOpen, setIsLineItemDuplicationModalOpen] = useState(false);
  const [isFormReady, setIsFormReady] = useState(id === NEW_ITEM_ID);

  const activeLineItem = useAppSelector(state => selectDV360TicketsLineItem(state, index));
  const isActiveTabSummary = useMemo(() => activeTab === LineItemFormTab.Summary, [activeTab]);

  useEffect(() => {
    if (activeLineItem) {
      const formData = dv360TicketsLineItemToFormStructure(activeLineItem);
      reset(formData);
      setSavedTabs(getDirtyTabs(formData));
      setIsFormReady(true);
    }
  }, [activeLineItem, reset]);

  const handleTabSelect = useCallback((name: LineItemFormTab) => {
    setActiveTab(name);
    setIsExpanded(true);
  }, []);

  const toggleTabExpansion = useCallback(() => setIsExpanded(prevExpanded => !prevExpanded), []);

  const handleSave = useCallback(async () => {
    if (!isActiveTabSummary && !isLineItemDuplicationModalOpen) {
      setActiveTab(LineItemFormTab.Summary);
      return;
    }

    setIsLoading(true);
    try {
      const ok = await onSave(getFormData());
      if (ok) {
        if (isLineItemDuplicationModalOpen) {
          setIsLineItemDuplicationModalOpen(false);
        }
        const dirtyTabs = getDirtyTabs(getValues() as IDV360TicketsLineItemForm);
        setSavedTabs(dirtyTabs);
      }
    } finally {
      setIsLoading(false);
    }
  }, [onSave, getFormData, isLineItemDuplicationModalOpen, isActiveTabSummary, getValues]);

  const loadCampaign = useCallback(() => {
    if (campaignId) {
      dispatch(
        getDv360TicketsInsertionOrderData({
          id: +campaignId,
        }),
      );
    }
  }, [dispatch, campaignId]);

  const onClickDuplicate = useCallback(() => setIsLineItemDuplicationModalOpen(true), []);
  const handleLineItemDuplicationModalClose = useCallback(
    (refresh?: boolean) => {
      if (refresh) {
        loadCampaign();
      }
      setIsLineItemDuplicationModalOpen(false);
    },
    [loadCampaign],
  );

  const ActiveTab = useMemo(() => DV360_LINE_ITEM_TABS_COMPONENTS[activeTab], [activeTab]);

  if (activeLineItem?.is_archived) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      {product && activeLineItem && (
        <DuplicateDV360TicketsLineItemModal
          isOpen={isLineItemDuplicationModalOpen}
          lineItemId={activeLineItem.id}
          product={product}
          handleClose={handleLineItemDuplicationModalClose}
        />
      )}
      <Paper className='col-span-3 mt-8 !bg-[#E4E8E6]' ref={ref}>
        <div className='flex justify-between items-center px-7 py-4'>
          <div className='flex flex-col gap-3 min-w-0'>
            <div
              className='flex items-center justify-between w-full text-left text-xl rounded-t-md font-semibold'
              onClick={toggleTabExpansion}
            >
              <div className='flex items-center gap-3 min-w-0'>
                <span className='font-semibold text-[#1E1E1E] text-xl cursor-pointer truncate' title={displayName}>
                  {displayName}
                </span>
                <div className='flex items-center gap-3 shrink-0'>
                  <div className='flex items-center gap-3' onClick={event => event.stopPropagation()}>
                    <DuplicateButton onClick={onClickDuplicate} disabled={!isValid} />
                    <DV360LineItemStatusChangeButton
                      activeLineItem={activeLineItem}
                      loading={isLoading}
                      setLoading={setIsLoading}
                      readOnly={readOnly}
                    />
                    <LineItemStatus status={activeLineItem?.status} />
                  </div>
                  {isExpanded ? (
                    <ExpandLess className={EXPAND_ICON_CLASSNAME} />
                  ) : (
                    <ExpandMore className={EXPAND_ICON_CLASSNAME} />
                  )}
                </div>
              </div>
            </div>
            <div className='flex gap-2'>
              {DV360_LINE_ITEM_TABS.map(name => (
                <UserTypeCheckbox
                  checked={name === activeTab}
                  key={name}
                  label={name}
                  name={name}
                  disabled={name === LineItemFormTab.Creatives && !product}
                  isCreateCampaign={true}
                  onClick={() => {
                    handleTabSelect(name);
                  }}
                  onChange={() => undefined}
                  labelIcon={
                    savedTabs.includes(name) && (
                      <CheckIcon className={'ml-1 flex item-center'} color={name === activeTab ? '#FFF' : '#737373'} />
                    )
                  }
                />
              ))}
            </div>
          </div>
          {isFormReady && <DV360TicketsForecasterWidget lineItemIndex={index} isExpanded={isExpanded} />}
        </div>
        <Collapse in={isExpanded} timeout={500}>
          <BlurBackdrop open={isLoading} backdropContent={<SavingDots />}>
            <form
              className='flex min-h-[740px] bg-[#FFF] py-4 px-7 flex-col justify-between relative h-full'
              onSubmit={handleSubmit(handleSave)}
            >
              {ActiveTab && <ActiveTab index={index} />}
              <div className='mt-4 flex items-center gap-2 justify-end'>
                {isActiveTabSummary ? (
                  <span>
                    By clicking <i>Confirm</i>, you agree that the above parameters are accurate and approved for
                    execution.
                  </span>
                ) : null}
                <Button
                  type='submit'
                  variant='contained'
                  className='bg-[#1B4332]'
                  sx={{
                    height: '48px',
                    width: '130px',
                  }}
                  disabled={!isValid || !isDirty || readOnly}
                >
                  {isActiveTabSummary ? 'Confirm' : 'Save'}
                </Button>
              </div>
            </form>
          </BlurBackdrop>
        </Collapse>
      </Paper>
    </FormProvider>
  );
});

export default DV360LineItemForm;
